// template-picker.tsx — a clean template SELECTOR for the message editor.
//
// Replaces the "reuse the management view" flow (which opened the template
// EDITOR on click). A picker with a live WhatsApp preview reads better: pick a
// template on the left, see exactly what it sends on the right, then "Use
// template" loads it into the editor (loadTemplateData — the same apply the
// legacy path used) and closes. Reuses the template store + the shared
// PreviewScheduleMessage, so media / polls / links preview exactly as they send.

import React, { useEffect, useMemo, useState } from 'react';
import { Search, Plus, Paperclip, Link as LinkIcon, BarChart3, MessageSquareText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { DBTemplateThunks, TEMPLATE_LIST_QUERY, templateSelectByQuery } from '@common-reducers/DBServiceThunks';
import { loadTemplateData } from '@common/reducers/ScheduledMessageEditorReducer';
import PreviewScheduleMessage from '@extension/context/content/components/add-scheduled-message-button/preview-schedule-message';
import { useCampaignsRedesignNav } from '@extension/context/content/components/campaigns-redesign/nav-context';
import type { Template } from '@common/models/template';
import { namespace } from '@common/config/constants';
import { Button } from '@/components/ui/button';

interface TemplatePickerProps {
  /** Close the picker dialog (called after a template is applied, or Cancel). */
  onClose: () => void;
}

export default function TemplatePicker({ onClose }: TemplatePickerProps) {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const redesignNav = useCampaignsRedesignNav();

  const templates = useAppSelector((state) =>
    templateSelectByQuery(state.DBTemplateReducer, { deleted: false }, [], { updatedAt: -1 }),
  ) as Template[];

  useEffect(() => {
    dispatch(DBTemplateThunks.find({ deleted: false, ...TEMPLATE_LIST_QUERY }));
  }, [dispatch]);

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (tpl) =>
        (tpl.name ?? '').toLowerCase().includes(q) ||
        (tpl.message ?? '').toLowerCase().includes(q),
    );
  }, [templates, search]);

  // Default the selection to the first template so the preview is never empty.
  useEffect(() => {
    // (Re)default the selection whenever the current one isn't in the filtered
    // set — on first load AND after a search narrows the selected template out,
    // so the preview never goes blank while matches are still listed.
    if (filtered.length > 0 && !filtered.some((tpl) => String(tpl._id ?? '') === selectedId)) {
      setSelectedId(String(filtered[0]._id ?? ''));
    }
  }, [filtered, selectedId]);

  const selected = useMemo(
    () => filtered.find((tpl) => String(tpl._id ?? '') === selectedId) ?? null,
    [filtered, selectedId],
  );

  const applyTemplate = () => {
    if (!selected) return;
    const { _id: _ignored, ...templateWithoutId } = selected;
    dispatch(loadTemplateData(templateWithoutId));
    onClose();
  };

  const createNew = () => {
    if (redesignNav) {
      onClose();
      redesignNav.newTemplate();
    }
  };

  return (
    <div className="flex h-full w-full gap-4 overflow-hidden">
      {/* LEFT — searchable template list */}
      <div className="flex w-[42%] min-w-[260px] flex-col gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('templates.searchPlaceholder')}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-sky-400 focus:bg-white dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-100 dark:focus:bg-slate-800"
          />
        </div>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 py-10 text-center text-sm text-slate-400">
              <MessageSquareText className="h-6 w-6 opacity-60" />
              {search ? t('templates.noResults', 'No templates match your search') : t('templates.empty', 'No templates yet')}
            </div>
          ) : (
            filtered.map((tpl) => {
              const id = String(tpl._id ?? '');
              const active = id === selectedId;
              const hasFile = !!tpl.asset?.url;
              const hasLink = !!tpl.linkPreview?.url;
              const isPoll = !!tpl.isPoll;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelectedId(id)}
                  className={[
                    'w-full rounded-xl border p-3 text-left transition-all',
                    active
                      ? 'border-sky-400 bg-sky-50 ring-1 ring-sky-400 dark:border-sky-500 dark:bg-sky-950/40'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600',
                  ].join(' ')}
                >
                  <div className="mb-1 flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {tpl.name || t('templates.unnamed')}
                    </span>
                    <div className="ml-auto flex flex-shrink-0 items-center gap-1 text-slate-400">
                      {isPoll && <BarChart3 className="h-3.5 w-3.5" />}
                      {hasFile && <Paperclip className="h-3.5 w-3.5" />}
                      {hasLink && !hasFile && <LinkIcon className="h-3.5 w-3.5" />}
                    </div>
                  </div>
                  <p className="line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
                    {tpl.message || (isPoll ? t('templates.pollPreview', 'Poll') : t('templates.unnamed'))}
                  </p>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT — the selected template in the SAME WhatsApp preview the
          schedule-message editor shows beside it (PreviewScheduleMessage), so a
          template looks identical here and once it is loaded into the editor. */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="min-h-0 flex-1">
          {selected ? (
            <PreviewScheduleMessage messageOverride={selected} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 p-5 text-center text-sm text-slate-400 dark:border-slate-700">
              <MessageSquareText className="h-7 w-7 opacity-50" />
              {t('templates.selectToPreview', 'Select a template to preview it here')}
            </div>
          )}
        </div>

        {/* actions */}
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={createNew}
            disabled={!redesignNav}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" /> {t('templates.newTemplate')}
          </Button>
          <div className="flex-1" />
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="button"
            onClick={applyTemplate}
            disabled={!selected}
            className="bg-sky-500 text-white hover:bg-sky-600"
          >
            {t('templates.useTemplate', 'Use template')}
          </Button>
        </div>
      </div>
    </div>
  );
}
